import { Identifiable, Sized, TaggedUnion } from "utils/types";
import { PanelMode } from "rc-picker/lib/interface";

export const DATE_FORMAT = "DD.MM.YYYY";
export const YEAR_FORMAT = "YYYY";
export const MONTH_FORMAT = "MM.YYYY";

export type UserProfile = {
  id: number;
  phone: string | null;
  name: string | null;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  middle_name: string | null;
  birthday: string | null;
  avatar: string | null;
  country: string | null;
  city: string | null;
  street: string | null;
  house_number: string | null;
  apartment_number: string | null;
  passport_id: string | null;
  identification_number: string | null;
  international_first_name: string | null;
  international_last_name: string | null;
  international_passport_id: string | null;
  international_passport_series: string | null;
};

export type Value = string;
export type Branching<Config = string, Default = string> = {
  branching?: {
    next?: Config;
    default: Default;
  };
};
export type InteractiveBlockItem = {
  name: string;
  link?: boolean;
  sort?: number;
  value?: Value;
};

export type Data = Record<string, unknown>;

/**
 * `link` being an url to needed resource
 */
export type MediaItem =
  | {
      type: "photo" | "video" | "audio";
      link: string;
    }
  | {
      type: "file";
      link: string;
      name?: string;
      size?: string;
    };

export type DialogContentTypeMap = {
  text: {
    text: string;
    checked?: boolean;
    markdown?: boolean;
    attached?: MediaItem[];
  }; // can attach list of links to media
};

export const CONTENT_TYPES = ["text", "link", "photo", "video", "audio", "file"] as const;

export type DialogContentBase = TaggedUnion<DialogContentTypeMap> | MediaItem;
export type DialogContent = { html?: boolean } & DialogContentBase;
export type DialogContentType = DialogContentBase["type"];

export type ContentBlockTypeMap = {
  cards: {
    textColor?: number;
    items: { title: string; description: string }[];
  };
  description: {
    textColor?: number;
    text: string;
    title: string;
    icon?: string;
  };
};

export type ContentBlockBase = TaggedUnion<ContentBlockTypeMap>;
export type ContentBlock = ContentBlockBase & { background?: string };
export type ContentBlockType = keyof ContentBlockTypeMap;

export const DESCRIPTION_BLOCK_TYPES = ["card", "check"] as const;
export const START_CHAT_BRANCH = "startsChat";
export const CONTINUE_BRANCH = "continue";
export const INITIAL_BRANCH = "initial";
export type Keyboard = {
  position?: "above" | "below";
  dynamicValues?: boolean;
  invalidate?: string;
  items: (InteractiveBlockItem & Partial<Sized>)[];
} & Identifiable<"blockName"> &
  Branching<{ value: string; branch: string }[]>;

export type SimpleInput = TaggedUnion<{
  numberInput: {
    placeholder?: string;
    required: boolean;
    format: boolean;
    validation?: {
      pattern: string;
      example?: string;
      message: string;
    }[];
  };
  input: {
    placeholder?: string;
    required: boolean;

    validation?: {
      pattern: string;
      example?: string;
      message: string;
    }[];
  };
  phone: {
    placeholder?: string;
    required: boolean;
  };
  checkbox: {};
  slider: {
    lowerBound: string;
    upperBound: string;
    step: string;
    required: boolean;
  };
  dropdown: {
    placeholder?: string;
    required: boolean;
    multiple: boolean;
    sort?: boolean;
    items?: InteractiveBlockItem[]; // explicit set of items
    itemsLink?: string; // link to dataset, if none will use inner api
    queryParams?: Record<string, string>;
    filterUkraine?: boolean;
    isGroup?: boolean;
    dropdownType: "simple" | "dictionary";
  };
  date: {
    placeholder?: string;
    defaultPickerDate?: string;
    initialPanel: PanelMode;
    disabledDate?: string;
    dateType: "date" | "time" | "datetime" | "year" | "month";
    dateReturnType: "string" | "object";
    isRange: boolean;
    required: boolean;
  };
  fileInput: { required: boolean };
}> & {
  resetOnEnter?: boolean;
  defaultValue?: string;
  title?: string;
  hidden?: string;
  disableBack?: string;
  invalidate?: string; // expr string that returns boolean if value should be invalidated
};

export type BitrixInfoItemConfig = { name: string; value: string };
export type BitrixDeal = {
  TITLE: string;
  CATEGORY_ID: string;
  TYPE_ID: string;
};

export type InteractiveBlockTypeMap = {
  fileInput: Omit<SimpleInput & { type: "fileInput" }, "type"> &
    Identifiable<"blockName"> & {
      keyboard?: Keyboard;
    };
  input: Omit<SimpleInput & { type: "input" }, "type"> &
    Identifiable<"blockName"> &
    Branching<{ value: string; branch: string }[]> & {
      keyboard?: Keyboard;
    };
  numberInput: Omit<SimpleInput & { type: "numberInput" }, "type"> &
    Identifiable<"blockName"> &
    Branching<{ value: string; branch: string }[]> & {
      keyboard?: Keyboard;
    };
  phone: Omit<SimpleInput & { type: "phone" }, "type"> &
    Identifiable<"blockName"> & {
      keyboard?: Keyboard;
    };
  suggestions: { text: string; skipable: boolean };
  authorize: { noPrompts: boolean };
  promocode: {};
  setContractData: { data?: string };
  bitrixChat: { initialMessage?: string };
  httpRequest: { options?: string; query?: string; uri: string; path?: string };
  sendEmail: {};
  searchCar: { regNumber: string; path: string };
  paymentLink: { path?: string; recurrent?: string };
  awaitPayment: {};
  addToCart: {
    product?: string;
    total?: string;
    data?: string;
    itemInfo: { name: string; value: string }[];
  };
  calculatePrice: { product?: string; data?: string; path?: string };
  setValue: { path: string; value: string; invalidate?: string };
  pushValue: { path: string; value: string; invalidate?: string };
  ppDocument: { data?: string; path?: string };
  signUpToRepresentativeOffice: {};
  verifyPhone: {};
  bitrixTrigger: BitrixDeal;
  validate: {
    yesButtonText?: string;
    text?: string;
    answersToValidate?: { blockName: string; title: string }[];
  };
  form: {
    inputs: (SimpleInput & Identifiable<"blockName"> & { fieldName?: string })[];
    allowDiia: boolean;
    submitText?: string;
  } & { keyboard?: Keyboard } & Partial<Identifiable<"blockName">>;
  signContract: {};

  button: { title?: string } & Keyboard;
  slider: Omit<SimpleInput & { type: "slider" }, "type"> &
    Identifiable<"blockName"> &
    Branching<{ nextBound: number; branch: string }[]> & {
      keyboard?: Keyboard;
    }; // given a range of values we may branch
  dropdown: Omit<SimpleInput & { type: "dropdown" }, "type"> &
    Identifiable<"blockName"> &
    Branching<{ value: string; branch: string }[]> & {
      keyboard?: Keyboard;
    };
  date: Omit<SimpleInput & { type: "date" }, "type"> &
    Identifiable<"blockName"> &
    Branching<{ nextBound: Date; branch: string }[]> & {
      keyboard?: Keyboard;
    }; // given a range of dates we may branch

  branch: { matchers: { next: string; rule: string }[] };
  clearCallStack: {};
  emitEvent: { name: string; data: string };
};

export const INTERACTIVE_BLOCK_TYPES = [
  "input",
  "button",
  "checkbox",
  "slider",
  "dropdown",
  "date",
  "signContract",
  "authorize",
  "confirm",
] as const;

export type InteractiveBlockBase = TaggedUnion<InteractiveBlockTypeMap>;
export type InteractiveBlock = InteractiveBlockBase;
export type InteractiveBlockType = keyof InteractiveBlockTypeMap;

export type DialogBlockTypeMap = {
  interactive: InteractiveBlockBase;
  content: DialogContentBase;
};

export type DialogBlockBase = TaggedUnion<DialogBlockTypeMap, "blockType">;
export type DialogBlock = DialogBlockBase;
export type DialogBlockType = keyof DialogBlockTypeMap;

export type DialogBlockType2 = TaggedUnion<
  {
    interactive: { type: InteractiveBlockType };
    content: { type: DialogContentType };
  },
  "blockType"
>;
export type ScenarioItem = {
  dialogBlock: DialogBlock;
  contentBlock?: ContentBlock;
};

// first scenario item must contain initial content block
export type Scenario = { [branchId: string]: ScenarioItem[] };
export type ProductTimeout = { delay: number; deal: BitrixDeal };
export type ProductScenario = {
  runnerVersion: number;
  background?: string;
  title?: string;
  scenario: Scenario;
  settings?: {
    bitrixConfig?: BitrixInfoItemConfig[][]; // custom data grid def
    paymentTimeout?: ProductTimeout;
    signTimeout?: ProductTimeout;
    signUpToRepresentativeOfficeTimeout?: ProductTimeout;
  };
};

export type Product = {
  background?: string;
  title?: string;
  translation: Record<string, string>;
  insurance: ProductScenario;
  incident: ProductScenario;
  relevant: { id: string; name: string; description: string; slug: string }[];
};

export enum OrderStatus {
  NotPaid = 1,
  NotYetBeenSigned = 2,
  Hold = 3,
  Expired = 4,
  Canceled = 5,
  Paid = 6,
  NotSigned = 7,
  Failed = 8,
  Signed = 9,
}
